import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { listBrands } from "@/api/brands";
import * as api from "@/api/reviews";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { extractErrorMessage } from "@/lib/api-errors";
import { mpProductUrl } from "@/lib/marketplace";
import type { ReviewQueueItem } from "@/types/api";

import { ReviewsTodaySummary } from "./ReviewsTodaySummary";

const STATUS_LABELS: Record<string, string> = {
  pending: "Ждёт одобрения",
  approved: "В очереди",
  posting: "Публикуется",
  posted: "Отправлено",
  verified: "Опубликовано",
  failed: "Ошибка",
  rejected: "Отклонён",
  needs_manual: "Нужен ручной ответ",
};

// Пункты фильтра: транзитные статусы публикации (секунды–минуты) — одним пунктом; бэк
// принимает список через запятую. «Отклонён» — только в «Все статусы».
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "pending", label: "Ждут одобрения" },
  { value: "needs_manual", label: "Нужен ручной ответ" },
  { value: "failed", label: "Ошибка публикации" },
  { value: "approved,posting,posted", label: "В публикации" },
  { value: "verified", label: "Опубликовано" },
];

const STATUS_CLASS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  approved: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  posting: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  posted: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  verified: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  rejected: "bg-muted text-muted-foreground",
  needs_manual: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
};

// Авто-заметка бэкенда для ответа на удалённый отзыв (`internal/router.py` REVIEW_DELETED_NOTE,
// миграция 0055) — менять вместе. Дальше может идти «. Исходная заметка: …» публикатора.
const AUTO_REJECT_REASON = "отзыв удалён на маркетплейсе";
const AUTO_REJECT_NOTE = `${AUTO_REJECT_REASON} — ответ отклонён автоматически`;

function isAutoRejected(item: ReviewQueueItem): boolean {
  return item.post_status === "rejected" && (item.post_note ?? "").startsWith(AUTO_REJECT_NOTE);
}

/** Почему ответ вернулся из публикации — сразу под строкой со статусом. */
function PostReason({ item }: { item: ReviewQueueItem }): React.ReactElement | null {
  if (!item.post_note) return null;
  if (item.post_status === "failed") {
    return (
      <div className="text-sm font-medium text-red-700 dark:text-red-400" data-testid="post-reason">
        Причина: {item.post_note}
      </div>
    );
  }
  if (isAutoRejected(item)) {
    const rest = item.post_note.slice(AUTO_REJECT_NOTE.length).replace(/^\.\s*/, "");
    return (
      <div className="text-xs text-muted-foreground" data-testid="post-reason">
        Отклонён автоматически: {AUTO_REJECT_REASON}
        {rest ? `. ${rest}` : ""}
      </div>
    );
  }
  return null;
}

function StatusChip({ status }: { status: string }): React.ReactElement {
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_CLASS[status] ?? "bg-muted"}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function ReviewQueuePage(): React.ReactElement {
  const qc = useQueryClient();
  const [platform, setPlatform] = useState("");
  const [postStatus, setPostStatus] = useState("pending");
  const [legal, setLegal] = useState("");
  const [brand, setBrand] = useState("");
  const [rating, setRating] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("new");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<{ id: string; text: string; note: string } | null>(null);

  // Debounce the search box so typing doesn't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const filters: api.QueueFilters = {
    platform: platform || undefined,
    post_status: postStatus || undefined,
    legal_name: legal || undefined,
    brand: brand || undefined,
    rating: rating ? Number(rating) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort,
    q: debouncedQ || undefined,
    limit: 100,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["reviews", filters],
    queryFn: () => api.listQueue(filters),
  });
  const items = data?.items ?? [];

  const { data: brands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: listBrands,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["reviews"] });
    qc.invalidateQueries({ queryKey: ["reviews-dashboard"] });
  };
  const clearSel = () => setSelected(new Set());

  const editMut = useMutation({
    mutationFn: (args: { id: string; text: string; note: string }) =>
      api.editReply(args.id, args.text, args.note),
    onSuccess: (res) => {
      invalidate();
      setEditing(null);
      if (res.issues.length) toast.warning("Сохранено, но есть замечания: " + res.issues.join("; "));
      else toast.success("Сохранено");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка сохранения")),
  });

  const publishMut = useMutation({
    mutationFn: api.publishReply,
    onSuccess: () => {
      invalidate();
      toast.success("Отправлено в очередь публикации");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка публикации")),
  });

  const rejectMut = useMutation({
    mutationFn: api.rejectReply,
    onSuccess: () => {
      invalidate();
      toast.success("Отклонён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка")),
  });

  const bulkPublishMut = useMutation({
    mutationFn: api.bulkPublish,
    onSuccess: (res) => {
      invalidate();
      clearSel();
      const skipped = res.skipped.length ? `, пропущено ${res.skipped.length}` : "";
      toast.success(`Опубликовано ${res.published}${skipped}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка массовой публикации")),
  });

  const bulkCancelMut = useMutation({
    mutationFn: api.bulkCancel,
    onSuccess: (res) => {
      invalidate();
      clearSel();
      toast.success(`Отменено ${res.cancelled}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка отмены")),
  });

  const regenerateMut = useMutation({
    mutationFn: api.regenerateReply,
    onSuccess: () => {
      invalidate();
      toast.success("Отправлено на перегенерацию");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка перегенерации")),
  });

  const bulkRegenerateMut = useMutation({
    mutationFn: api.bulkRegenerate,
    onSuccess: (res) => {
      invalidate();
      clearSel();
      toast.success(`Отправлено на перегенерацию: ${res.regenerated}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Ошибка массовой перегенерации")),
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.reply_id));
  const toggleAll = () => {
    if (allSelected) clearSel();
    else setSelected(new Set(items.map((i) => i.reply_id)));
  };

  const selectedIds = [...selected];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Отзывы</h1>
        <p className="text-sm text-muted-foreground">
          Сгенерированные ответы на неотвеченные отзывы. Проверьте, при необходимости
          отредактируйте и опубликуйте. Публикация в маркетплейс идёт в фоне с проверкой факта.
        </p>
      </div>

      <ReviewsTodaySummary
        onShowStatus={(p, status) => {
          setPlatform(p);
          setPostStatus(status);
        }}
      />

      {/* Фильтры */}
      <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
        <div className="flex gap-1">
          {[
            { v: "", l: "Все площадки" },
            { v: "wb", l: "WB" },
            { v: "ozon", l: "Ozon" },
          ].map((p) => (
            <Button
              key={p.v}
              size="sm"
              variant={platform === p.v ? "default" : "outline"}
              onClick={() => setPlatform(p.v)}
            >
              {p.l}
            </Button>
          ))}
        </div>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={postStatus}
          onChange={(e) => setPostStatus(e.target.value)}
        >
          <option value="">Все статусы</option>
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Input
          className="h-9 w-44"
          placeholder="Юрлицо"
          value={legal}
          onChange={(e) => setLegal(e.target.value)}
        />
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
        >
          <option value="">Все бренды</option>
          {brands.map((b) => (
            <option key={b.id} value={b.display_name}>
              {b.display_name}
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={rating}
          onChange={(e) => setRating(e.target.value)}
        >
          <option value="">Все оценки</option>
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>
              {"★".repeat(r)} ({r})
            </option>
          ))}
        </select>
        <DateRangeFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          placeholder="Период отзыва"
          title="Период отзыва (дата на МП)"
          onChange={(from, to) => {
            setDateFrom(from);
            setDateTo(to);
          }}
        />
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          title="Сортировка по дате отзыва"
        >
          <option value="old">Сначала старые</option>
          <option value="new">Сначала новые</option>
        </select>
        <Input
          className="h-9 w-56"
          placeholder="Поиск по тексту отзыва"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="ml-auto text-sm text-muted-foreground">
          Всего: {data?.total ?? 0}
        </div>
      </div>

      {/* Массовые действия */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-md border bg-card p-2">
          <span className="text-sm">Выбрано: {selected.size}</span>
          <Button
            size="sm"
            onClick={() => bulkPublishMut.mutate(selectedIds)}
            disabled={bulkPublishMut.isPending}
          >
            Опубликовать выбранные
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulkCancelMut.mutate(selectedIds)}
            disabled={bulkCancelMut.isPending}
          >
            Отменить выбранные
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulkRegenerateMut.mutate(selectedIds)}
            disabled={bulkRegenerateMut.isPending}
          >
            На перегенерацию
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSel}>
            Снять выделение
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          Выбрать все на странице
        </label>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">Нет отзывов по фильтру.</p>
      )}

      <ul className="space-y-3">
        {items.map((item) => (
          <ReviewRow
            key={item.reply_id}
            item={item}
            selected={selected.has(item.reply_id)}
            onToggle={() => toggle(item.reply_id)}
            editing={
              editing?.id === item.reply_id
                ? { text: editing.text, note: editing.note }
                : null
            }
            onStartEdit={() =>
              setEditing({
                id: item.reply_id,
                text: item.final_text ?? item.proposed_text,
                note: item.operator_note ?? "",
              })
            }
            onChangeEdit={(patch) =>
              setEditing((prev) => (prev ? { ...prev, ...patch } : prev))
            }
            onCancelEdit={() => setEditing(null)}
            onSaveEdit={() =>
              editing &&
              editMut.mutate({ id: editing.id, text: editing.text, note: editing.note })
            }
            onPublish={() => publishMut.mutate(item.reply_id)}
            onReject={() => rejectMut.mutate(item.reply_id)}
            onRegenerate={() => regenerateMut.mutate(item.reply_id)}
          />
        ))}
      </ul>
    </div>
  );
}

interface RowProps {
  item: ReviewQueueItem;
  selected: boolean;
  onToggle: () => void;
  editing: { text: string; note: string } | null;
  onStartEdit: () => void;
  onChangeEdit: (patch: { text?: string; note?: string }) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onPublish: () => void;
  onReject: () => void;
  onRegenerate: () => void;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ProductInfo({ item }: { item: ReviewQueueItem }): React.ReactElement {
  const title = item.internal_name || item.product_name || "товар";
  const url = mpProductUrl(item.platform, item.product_sku);
  const mpLabel = item.platform === "wb" ? "WB" : "Ozon";

  const titleNode = url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
      title={`открыть на ${mpLabel}`}
    >
      <span className="truncate">{title}</span>
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
    </a>
  ) : (
    <span className="truncate font-medium text-foreground">{title}</span>
  );

  // The title already prefers internal_name; only show the marketplace product_name
  // separately when it differs from the title to avoid duplicating the same string.
  const showProductName = !!item.product_name && item.product_name !== title;

  return (
    <div className="min-w-0 space-y-0.5">
      <div className="min-w-0 text-sm">{titleNode}</div>
      {url ? (
        <div className="text-xs text-muted-foreground">открыть на {mpLabel}</div>
      ) : null}
      {showProductName && (
        <div className="truncate text-xs text-muted-foreground">{item.product_name}</div>
      )}
      {item.internal_article && (
        <div className="truncate text-xs text-muted-foreground">
          наш арт.: {item.internal_article}
        </div>
      )}
    </div>
  );
}

function ReviewRow(props: RowProps): React.ReactElement {
  const { item } = props;
  const isNeedsManual = item.post_status === "needs_manual";
  const canPublish = item.post_status === "pending" || item.post_status === "failed";
  const canReject = !["posted", "verified", "posting"].includes(item.post_status);
  const canRegenerate = !["posted", "verified", "posting"].includes(item.post_status);
  const text = item.final_text ?? item.proposed_text;
  const critic = item.critic;

  return (
    <li className="rounded-md border p-3">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={props.selected}
          onChange={props.onToggle}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold">{"★".repeat(item.rating ?? 0) || "—"}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
              {item.platform === "wb" ? "WB" : "Ozon"}
            </span>
            <span className="text-muted-foreground">{item.legal_name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
              бренд: {item.brand ?? "—"}
            </span>
            <StatusChip status={item.post_status} />
            {item.human_edited && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs">правлено</span>
            )}
            <span className="ml-auto text-xs text-muted-foreground">
              отзыв: {fmtDateTime(item.created_on_mp)}
            </span>
          </div>

          <PostReason item={item} />

          {isNeedsManual && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <div className="font-medium">
                {item.brand
                  ? `бренд не настроен: ${item.brand}`
                  : "бренд не определён"}
              </div>
              {item.hard_issues.length > 0 && (
                <div className="mt-0.5">{item.hard_issues[0]}</div>
              )}
              <div className="mt-1 text-amber-700 dark:text-amber-400">
                Ответьте вручную (отредактируйте и сохраните) либо отправьте на перегенерацию
                после настройки бренда.
              </div>
            </div>
          )}

          <ProductInfo item={item} />

          {(item.review_text || item.review_pros || item.review_cons) && (
            <div className="max-h-32 space-y-1 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-2 text-sm">
              {item.review_text ? (
                <div>
                  {item.buyer_name ? <b>{item.buyer_name}: </b> : null}
                  {item.review_text}
                </div>
              ) : (
                item.buyer_name && <div><b>{item.buyer_name}</b></div>
              )}
              {item.review_pros && (
                <div>
                  <b className="text-emerald-600 dark:text-emerald-400">Плюсы:</b> {item.review_pros}
                </div>
              )}
              {item.review_cons && (
                <div>
                  <b className="text-rose-600 dark:text-rose-400">Минусы:</b> {item.review_cons}
                </div>
              )}
            </div>
          )}

          {/* Ответ */}
          {props.editing !== null ? (
            <div className="space-y-2">
              <Textarea
                className="min-h-[120px]"
                value={props.editing.text}
                onChange={(e) => props.onChangeEdit({ text: e.target.value })}
              />
              <Textarea
                className="min-h-[56px]"
                placeholder="Комментарий редактора (необязательно): причина правки / заметка для промпта"
                value={props.editing.note}
                onChange={(e) => props.onChangeEdit({ note: e.target.value })}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={props.onSaveEdit}>
                  Сохранить
                </Button>
                <Button size="sm" variant="ghost" onClick={props.onCancelEdit}>
                  Отмена
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded border border-dashed p-2 text-sm whitespace-pre-wrap">
              {text ? (
                text
              ) : (
                <span className="text-muted-foreground italic">
                  Ответ не сгенерирован — введите текст вручную через «Редактировать».
                </span>
              )}
              <div className="mt-1 text-xs text-muted-foreground">
                {item.reply_chars} симв.
                {text ? ` · сгенерирован ${fmtDateTime(item.created_at)}` : ""}
              </div>
            </div>
          )}

          {/* Оригинал генерации до ручной правки */}
          {props.editing === null &&
            item.human_edited &&
            item.final_text &&
            item.final_text !== item.proposed_text && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">оригинал генерации (до правки)</summary>
                <div className="mt-1 whitespace-pre-wrap rounded bg-muted/30 p-2">
                  {item.proposed_text}
                </div>
              </details>
            )}

          {/* Замечания валидатора и критика */}
          {Array.isArray(item.hard_issues) && item.hard_issues.length > 0 && (
            <div className="text-xs text-red-700 dark:text-red-400">⚠ {item.hard_issues.map(String).join("; ")}</div>
          )}
          {critic && critic.severity && critic.severity !== "ok" && (
            <details className="text-xs text-amber-700 dark:text-amber-400">
              <summary className="cursor-pointer select-none">критик ({critic.severity})</summary>
              <div className="mt-1">
                {Array.isArray(critic.issues) && critic.issues.length
                  ? critic.issues.map(String).join("; ")
                  : critic.summary ?? ""}
              </div>
            </details>
          )}
          {item.operator_note && (
            <div className="text-xs text-blue-700 dark:text-blue-400">💬 редактор: {item.operator_note}</div>
          )}
          {item.post_note && item.post_status !== "failed" && !isAutoRejected(item) && (
            <div className="text-xs text-muted-foreground">заметка: {item.post_note}</div>
          )}

          {/* Действия */}
          {props.editing === null && (
            <div className="flex flex-wrap gap-2 pt-1">
              {canPublish && (
                <Button size="sm" onClick={props.onPublish}>
                  Опубликовать
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={props.onStartEdit}>
                Редактировать
              </Button>
              {canRegenerate && (
                <Button size="sm" variant="outline" onClick={props.onRegenerate}>
                  На перегенерацию
                </Button>
              )}
              {canReject && (
                <Button size="sm" variant="ghost" onClick={props.onReject}>
                  Отклонить
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
